import React, { useEffect, useReducer } from "react";
import { Dialog, DialogTitle, DialogContent, Tabs, Tab, Box, Button } from "@mui/material";
import axiosClient from "../../api/axiosClient.js";
import { CalendarEvent } from "../../api/types.tsx";
import { matchReducer, initialMatchState } from "./matchReducer.ts";
import MatchSummaryTab from "./tabs/MatchSummaryTab.tsx";
import MatchSetResultsTab from "./tabs/MatchSetResultsTab.tsx";
import MatchLineupTab from "./tabs/MatchLineupTab.tsx";
import { playerService } from "../../api/service.ts";
import MessageDialog from "../MessageDialog.tsx";

interface MatchResultDialogProps {
    open: boolean;
    onClose: () => void;
    calendarEvent?: CalendarEvent;
}

export default function MatchResultDialog({ open, onClose, calendarEvent: calendar }: MatchResultDialogProps) {
    const [tab, setTab] = React.useState(0);
    const safeCalendar = calendar!;
    //const [state, dispatch] = useReducer(matchReducer, initialMatchState(calendar, []));
    const [state, dispatch] = useReducer(
        matchReducer,
        { calendar: safeCalendar, players: [] },         // <--- initial arg
        ({ calendar }) => initialMatchState(calendar, []) // <--- lazy init
    );
    const [messageDialog, setMessageDialog] = React.useState({
        open: false,
        message: "",
        severity: "success" as "success" | "error",
    });

    useEffect(() => {
        if (!open || !calendar) return;

        const newState = initialMatchState(calendar, state.players ?? []);
        dispatch({ type: "RESET_STATE", payload: newState });

        playerService.getPlayers()
            .then(players => dispatch({ type: "SET_PLAYERS", players }))
            .catch(() => { });
        //alert(JSON.stringify(calendar));
    }, [open, calendar]);



    const save = async () => {
        try {
            const { teamWins, opponentWins } = state.sets.reduce(
                (acc, s) => {
                    if (s.teamScore > s.opponentScore) acc.teamWins++;
                    else if (s.opponentScore > s.teamScore) acc.opponentWins++;
                    return acc;
                },
                { teamWins: 0, opponentWins: 0 }
            );

            const finalResult = `${teamWins}-${opponentWins}`;

            const data = {
                matchId: state.match.id,
                eventId: calendar?.eventId,
                date: state.match.date,
                finalResult,
                competitionType: state.match.competitionType,
                notes: state.match.note,
                sets: state.sets,
                participants: state.participants,
            };

            await axiosClient.post("/matches/create", data);

            setMessageDialog({
                open: true,
                message: "Match saved successfully",
                severity: "success",
            });

            onClose();
        } catch (error: any) {
            console.error("Save error:", error);

            // Estrae il dettaglio dell’errore axios
            const detail =
                error.response?.data?.message ||
                error.response?.data ||
                error.message ||
                "Unknown error";

            setMessageDialog({
                open: true,
                message: `Error saving match: ${detail}`,
                severity: "error",
            });
        }
    };



    return (
        <>
            <Dialog fullScreen open={open} onClose={onClose} fullWidth maxWidth="lg">
                <DialogTitle sx={{ backgroundColor: "primary.main" }}>Insert Match Result</DialogTitle>
                <DialogContent>
                    <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
                        <Tab label="Summary" />
                        <Tab label="Sets" />
                        <Tab label="Lineups" />
                    </Tabs>

                    {tab === 0 && <MatchSummaryTab state={state} dispatch={dispatch} />}
                    {tab === 1 && <MatchSetResultsTab state={state} dispatch={dispatch} />}
                    {tab === 2 && <MatchLineupTab state={state} dispatch={dispatch} />}

                    <Box display="flex" justifyContent="flex-end" mt={2}>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button variant="contained" sx={{ ml: 2 }} onClick={save}>
                            Save
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
            <MessageDialog
                open={messageDialog.open}
                message={messageDialog.message}
                severity={messageDialog.severity as any}
                onClose={() => setMessageDialog({ ...messageDialog, open: false })}
            />
        </>
    );
}
